import { Timestamp } from "firebase/firestore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import {
  adminMarkBillingPaid,
  adminRecalculateBilling,
  adminSendInvoiceEmail,
  adminSetTestPartner,
  adminWaiveBilling,
} from "@/lib/callables";
import type { SellerBilling, SellerBillingStatus } from "@/types";
import { fetchCollection, orderBy, where } from "./use-firestore";

/**
 * As faturas de um mês, de todos os vendedores.
 *
 * A consulta é só de igualdade (`year` e `month`), sem `orderBy`: o Firestore
 * resolve dois filtros de igualdade pelos índices de campo único, e a ordenação
 * fica em memória pelo motivo de sempre, que é ele omitir o documento a que
 * falta o campo de ordenação.
 */
export function useBillingMonth(year: number, month: number) {
  return useQuery({
    queryKey: queryKeys.billingMonth(year, month),
    queryFn: () =>
      fetchCollection<SellerBilling>(
        "sellersBilling",
        where("year", "==", year),
        where("month", "==", month),
      ),
    select: (rows) =>
      [...rows].sort((a, b) => {
        // Em aberto primeiro: é o que exige ação.
        if (Boolean(a.paid) !== Boolean(b.paid)) return a.paid ? 1 : -1;
        return (b.totalAmount ?? b.platformFee ?? 0) - (a.totalAmount ?? a.platformFee ?? 0);
      }),
  });
}

/**
 * As faturas vencidas e não pagas, de toda a plataforma.
 *
 * Aqui o `orderBy` é seguro: `paid` e `dueDate` são escritos pelo backend em
 * todo documento de fatura, sem buraco. É a consulta do índice composto
 * `paid` mais `dueDate`.
 */
export function useOverdueBillings() {
  return useQuery({
    queryKey: queryKeys.billingOverdue(),
    queryFn: () =>
      fetchCollection<SellerBilling>(
        "sellersBilling",
        where("paid", "==", false),
        where("dueDate", "<=", Timestamp.fromDate(new Date())),
        orderBy("dueDate", "asc"),
      ),
    select: (rows) =>
      // Fatura absorvida por outra não é dívida própria: ela é quitada pela
      // cascata da fatura que a absorveu, e mostrá-la aqui contaria a mesma
      // dívida duas vezes.
      rows.filter((r) => !r.accumulatedIntoBillingId),
  });
}

/** O status de faturamento de todos os vendedores, para o corte por situação. */
export function useBillingStatuses() {
  return useQuery({
    queryKey: queryKeys.billingStatuses(),
    queryFn: () => fetchCollection<SellerBillingStatus>("sellersBillingStatus"),
    select: (rows) =>
      [...rows].sort((a, b) =>
        (a.sellerName ?? "").localeCompare(b.sellerName ?? "", "pt-BR"),
      ),
  });
}

/** Invalida tudo que uma escrita de faturamento pode ter mudado. */
function invalidateBilling(qc: ReturnType<typeof useQueryClient>, sellerId?: string) {
  qc.invalidateQueries({ queryKey: ["billing"] });
  qc.invalidateQueries({ queryKey: ["dashboard"] });
  if (sellerId) {
    qc.invalidateQueries({ queryKey: queryKeys.sellerBilling(sellerId) });
    qc.invalidateQueries({ queryKey: queryKeys.sellerBillingStatus(sellerId) });
    qc.invalidateQueries({ queryKey: queryKeys.seller(sellerId) });
  }
}

export function useMarkBillingPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { billingId: string; paid: boolean; reason?: string; sellerId?: string }) =>
      adminMarkBillingPaid({
        billingId: input.billingId,
        paid: input.paid,
        reason: input.reason,
      }),
    // O backend desbloqueia o estabelecimento em seguida, pelo trigger
    // onPaymentConfirmed, então a lista de vendedores também muda.
    onSuccess: (_result, input) => {
      invalidateBilling(qc, input.sellerId);
      qc.invalidateQueries({ queryKey: queryKeys.sellers() });
    },
  });
}

export function useWaiveBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { billingId: string; waived: boolean; reason?: string; sellerId?: string }) =>
      adminWaiveBilling({
        billingId: input.billingId,
        waived: input.waived,
        reason: input.reason,
      }),
    // Ignorar pode quitar a última dívida vencida, e o trigger
    // onPaymentConfirmed desbloqueia o estabelecimento em seguida.
    onSuccess: (_result, input) => {
      invalidateBilling(qc, input.sellerId);
      qc.invalidateQueries({ queryKey: queryKeys.sellers() });
    },
  });
}

export function useSetTestPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sellerId: string; isTestPartner: boolean }) =>
      adminSetTestPartner(input),
    onSuccess: (_result, input) => {
      invalidateBilling(qc, input.sellerId);
      // A lista de vendedores lê os status por esta chave própria.
      qc.invalidateQueries({ queryKey: ["sellersBillingStatus"] });
      qc.invalidateQueries({ queryKey: queryKeys.sellers() });
    },
  });
}

export function useRecalculateBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sellerId: string; months?: number }) =>
      adminRecalculateBilling(input),
    onSuccess: (_result, input) => invalidateBilling(qc, input.sellerId),
  });
}

export function useSendInvoiceEmail() {
  return useMutation({
    mutationFn: (input: {
      sellerId: string;
      month: number;
      year: number;
      recipientEmail?: string;
      preview?: boolean;
    }) => adminSendInvoiceEmail(input),
  });
}

/** Os últimos doze meses, do mais recente para o mais antigo. */
export function recentMonths(count = 12): { year: number; month: number; label: string }[] {
  const now = new Date();
  const months: { year: number; month: number; label: string }[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }),
    });
  }
  return months;
}
